import { Link } from "react-router-dom";
import { useCookieConsent } from "../../context/CookieConsentContext";

/* ══════════════════════════════════════════════════════════════════
   CookieBanner — Bandeau cookies RGPD SchoolSenses
   ══════════════════════════════════════════════════════════════════
   État géré par CookieConsentContext :
     • "accepted" → Vercel Analytics + SpeedInsights activés
     • "rejected" → seuls les cookies strictement nécessaires restent
   ══════════════════════════════════════════════════════════════════ */

export default function CookieBanner() {
  const { isVisible, setConsent } = useCookieConsent();

  if (!isVisible) return null;

  return (
    <section
      aria-live="polite"
      aria-label="Bandeau d'information sur les cookies"
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl shadow-2xl ring-1 ring-white/10 bg-slate-900/95 backdrop-blur text-slate-100 overflow-hidden">
        {/* Bande accent en haut */}
        <div className="h-1 bg-gradient-to-r from-accent via-orange-400 to-accent" />

        <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Icône */}
          <div className="flex-shrink-0 hidden sm:flex w-11 h-11 rounded-xl bg-accent/15 items-center justify-center">
            <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z" />
              <circle cx="9" cy="11" r="1" fill="currentColor" />
              <circle cx="14" cy="14" r="1" fill="currentColor" />
              <circle cx="15.5" cy="9" r="1" fill="currentColor" />
            </svg>
          </div>

          {/* Texte */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="sm:hidden inline-flex w-8 h-8 rounded-lg bg-accent/15 items-center justify-center">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z" />
                  <circle cx="9" cy="11" r="1" fill="currentColor" />
                  <circle cx="14" cy="14" r="1" fill="currentColor" />
                  <circle cx="15.5" cy="9" r="1" fill="currentColor" />
                </svg>
              </span>
              <h2 className="text-sm sm:text-base font-semibold text-white">
                Vos données, vos choix.
              </h2>
            </div>
            <p className="mt-1.5 text-xs sm:text-sm text-slate-300 leading-relaxed">
              Les <strong className="text-white font-semibold">cookies strictement nécessaires</strong>
              {" "}(session, sécurité, préférences) sont toujours actifs. Avec votre accord, nous activons
              aussi des <strong className="text-white font-semibold">cookies de mesure d'audience</strong>
              {" "}(Vercel Analytics) pour améliorer le service. Aucune publicité, aucun traceur tiers.
              Conforme <span className="whitespace-nowrap">RGPD</span>.{" "}
              <Link
                to="/privacy#cookies"
                className="text-accent hover:text-orange-300 underline underline-offset-2 font-medium"
              >
                En savoir plus
              </Link>
              .
            </p>
          </div>

          {/* Actions */}
          <div className="flex sm:flex-col gap-2 sm:gap-2 sm:w-auto w-full">
            <button
              type="button"
              onClick={() => setConsent("rejected")}
              className="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium rounded-lg border border-slate-600 text-slate-200 hover:bg-slate-800 hover:border-slate-500 transition-colors"
            >
              Refuser
            </button>
            <button
              type="button"
              onClick={() => setConsent("accepted")}
              className="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg bg-accent text-white hover:bg-orange-600 shadow-md shadow-accent/20 transition-colors"
            >
              Accepter
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
